import { Component, EventEmitter, OnChanges, Output, Signal, ViewChild, computed, input } from '@angular/core';

import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { smartString } from '@r1-compass-library/utilities';
import { TypeaheadSearchComponent } from '../typeahead-search/typeahead-search.component';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AssetMetadata, BasicFolder, isAsset, isFolder } from '@r1-compass-library/models';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FileTableEntry } from './file-table.model';
import { DatePipe } from '@angular/common';

export type RowOptionAction = {
    value: string & smartString;
    display: string;
};

export type RowOptionChoice = RowOptionAction & {
    index: number;
};

export type TableColumn = {
    columnDef: string;
    header: string;
    type: 'action' | 'string' | 'dependencyCount' | 'date' | 'boolean' | 'number';
    actions?: RowOptionAction[];
};

export type FileTableItem = (BasicFolder | AssetMetadata) & {
    dependencyCount?: number;
};

@Component({
    selector: 'r1-compass-lib-file-table',
    imports: [
        MatTableModule,
        MatPaginatorModule,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        TypeaheadSearchComponent,
        MatSortModule,
        DatePipe,
    ],
    templateUrl: './file-table.component.html',
    styleUrl: './file-table.component.scss',
})
export class FileTableComponent implements OnChanges {
    columns = input.required<TableColumn[]>();
    nonActionColumns: Signal<TableColumn[]> = computed(() => this.columns().filter(column => column.type !== 'action'));
    actionColumns: Signal<TableColumn[]> = computed(() => this.columns().filter(column => column.type === 'action'));

    fileTableData = input.required<FileTableEntry[]>();

    displayedColumns: Signal<string[]> = computed(() => this.columns().map(column => column.columnDef));
    dataSource = new MatTableDataSource<FileTableEntry>([]);
    searchedFilesAndFolders = input.required<FileTableItem[]>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatTable) table!: MatTable<FileTableItem>;
    @ViewChild(MatSort) sort!: MatSort;

    valueFormControl = new FormControl('');
    displayFormControl = new FormControl('');

    @Output()
    searchFilesAndFolders = new EventEmitter<string>();

    @Output()
    folderChange = new EventEmitter<BasicFolder>();

    @Output()
    editAsset = new EventEmitter<AssetMetadata>();

    @Output()
    clickOption = new EventEmitter<{ optionValue: string; item: FileTableItem }>();

    @Output()
    openDependencies = new EventEmitter<AssetMetadata>();

    ngOnChanges() {
        this.dataSource = new MatTableDataSource<FileTableEntry>(this.fileTableData());
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
        this.dataSource.sortingDataAccessor = (fileTableEntry, sortHeader) => {
            const casted = sortHeader as keyof FileTableEntry;
            if (typeof fileTableEntry[casted] === 'number') {
                return fileTableEntry[casted] as number;
            } else {
                const val = String(fileTableEntry[casted]);
                return val.toUpperCase();
            }
        };
    }

    onClearSearch() {
        // Clears Typeahead search results if user backspaces to 0 characters.
        this.searchFilesAndFolders.emit('');
    }

    onTypeaheadSearch(search: string) {
        this.searchFilesAndFolders.emit(search);
    }

    onTypeaheadSelection(event: MatAutocompleteSelectedEvent) {
        const value: FileTableItem | undefined = event.option.value;
        if (value) {
            if (isFolder(value)) {
                this.folderChange.emit(value as BasicFolder);
            } else if (isAsset(value)) {
                this.editAsset.emit(value as AssetMetadata);
            }
        }
    }

    onRowSelect(row: FileTableEntry, column: TableColumn) {
        if (column.type === 'action') {
            return;
        }
        if (column.type === 'dependencyCount' && !isFolder(row.item)) {
            // Don't want the rowClick to be disabled in the Dependencies column on Folder rows, just asset rows
            return;
        }
        if (isFolder(row.item)) {
            this.folderChange.emit(row.item as BasicFolder);
        } else if (isAsset(row.item)) {
            this.editAsset.emit(row.item as AssetMetadata);
        }
    }
}
